import { MAX_INVOICE_NAME_ALIASES } from "@albertine/types";
import { zodResolver } from "@hookform/resolvers/zod";
import { Link, X } from "lucide-react";
import { useReducer, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import { emptyToUndefined } from "@/utils/format.util";
import { FormField, FormInput } from "../../form-field/FormField";
import type { StripeCustomer } from "../data/profile";
import { useMemberProfile } from "../MemberProfileContext";
import { OwnedField } from "./FormFields";
import { FormShell } from "./ProfileOverlayShell";
import type { FormProps } from "./types";

const paymentsFormSchema = z.object({
    paymentsMustKnow: z.string(),
    preferredCurrency: z.string(),
    stripeCustomerId: z.string(),
    invoiceNameAliases: z.array(z.string()),
});

type PaymentsFormValues = z.infer<typeof paymentsFormSchema>;

export function PaymentsForm({ onClose }: FormProps) {
    const { profile, savePayments, isAdmin, findStripeCustomer } = useMemberProfile();
    const { control, handleSubmit } = useForm<PaymentsFormValues>({
        resolver: zodResolver(paymentsFormSchema),
        defaultValues: {
            paymentsMustKnow: profile.payments.paymentsMustKnow ?? "",
            preferredCurrency: profile.payments.preferredCurrency ?? "",
            stripeCustomerId: profile.payments.stripe?.id ?? "",
            invoiceNameAliases: profile.payments.invoiceNameAliases ?? [],
        },
    });
    const [stripeLookupState, dispatchStripeLookup] = useReducer(stripeLookupReducer, {
        status: "idle",
    });
    const stripeCustomer = stripeCustomerForLookup(
        stripeLookupState,
        profile.payments.stripe,
    );
    const finding = stripeLookupState.status === "finding";
    const notFound =
        stripeLookupState.status === "resolved" && !stripeLookupState.customer;

    function onSubmit(values: PaymentsFormValues) {
        const stripeCustomerId = values.stripeCustomerId.trim();
        const paymentsMustKnow = emptyToUndefined(values.paymentsMustKnow);
        const preferredCurrency = emptyToUndefined(values.preferredCurrency);

        savePayments(
            {
                paymentsMustKnow,
                preferredCurrency,
                stripeCustomerId: stripeCustomerId || undefined,
                invoiceNameAliases: values.invoiceNameAliases,
            },
            onClose,
        );
    }

    async function onFind(stripeCustomerId: string) {
        const trimmed = stripeCustomerId.trim();
        if (!trimmed) return;
        dispatchStripeLookup({ type: "find" });
        const customer = await findStripeCustomer(trimmed);
        dispatchStripeLookup({ type: "resolved", customer });
    }

    return (
        <FormShell
            title="Edit payments"
            onClose={onClose}
            onSave={handleSubmit(onSubmit)}
        >
            <OwnedField
                id="payments-must-know"
                label="Must know"
                kind="textarea"
                control={control}
                name="paymentsMustKnow"
            />
            <OwnedField
                id="payments-preferred-currency"
                label="Preferred currency"
                kind="text"
                control={control}
                name="preferredCurrency"
            />
            <Controller
                control={control}
                name="invoiceNameAliases"
                render={({ field }) => (
                    <InvoiceNamesField
                        aliases={field.value}
                        onChange={field.onChange}
                    />
                )}
            />
            <Controller
                control={control}
                name="stripeCustomerId"
                render={({ field, fieldState }) => (
                    <FormField
                        label="Stripe"
                        htmlFor="payments-stripe"
                        error={fieldState.error?.message}
                    >
                        <div className="flex gap-2">
                            <FormInput
                                id="payments-stripe"
                                name={field.name}
                                ref={field.ref}
                                className="min-w-0 flex-1"
                                value={String(field.value ?? "")}
                                placeholder="Stripe customer id"
                                readOnly={!isAdmin}
                                aria-invalid={fieldState.invalid || undefined}
                                onBlur={field.onBlur}
                                onChange={(event) => {
                                    field.onChange(event);
                                    dispatchStripeLookup({ type: "typed" });
                                }}
                            />
                            {isAdmin && (
                                <Button
                                    type="button"
                                    variant="outline"
                                    disabled={!!stripeCustomer || finding}
                                    onClick={() => {
                                        void onFind(field.value);
                                    }}
                                >
                                    Find
                                </Button>
                            )}
                        </div>
                        <StripeLookupResult
                            finding={finding}
                            customer={stripeCustomer}
                            notFound={notFound}
                        />
                    </FormField>
                )}
            />
        </FormShell>
    );
}

function InvoiceNamesField({
    aliases,
    onChange,
}: {
    aliases: string[];
    onChange: (aliases: string[]) => void;
}) {
    const [draft, setDraft] = useState("");
    const [error, setError] = useState<string>();

    function addAlias() {
        const trimmed = draft.trim();
        if (!trimmed) {
            return;
        }
        if (aliases.length >= MAX_INVOICE_NAME_ALIASES) {
            setError(`You can add up to ${MAX_INVOICE_NAME_ALIASES} invoice names`);
            return;
        }
        const alreadySaved = aliases.some(
            (alias) => alias.toLowerCase() === trimmed.toLowerCase(),
        );
        if (alreadySaved) {
            setError("This invoice name is already added");
            return;
        }
        onChange([...aliases, trimmed]);
        setDraft("");
    }

    return (
        <FormField label="Invoice names" error={error}>
            {aliases.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                    {aliases.map((alias) => (
                        <Badge key={alias} variant="secondary">
                            {alias}
                            <button
                                type="button"
                                className="cursor-pointer text-zinc-500 hover:text-zinc-900"
                                onClick={() =>
                                    onChange(aliases.filter((item) => item !== alias))
                                }
                            >
                                <X className="size-3" />
                            </button>
                        </Badge>
                    ))}
                </div>
            )}
            <div className="flex gap-2">
                <FormInput
                    id="payments-invoice-name"
                    className="min-w-0 flex-1"
                    value={draft}
                    placeholder="Company or alternate invoice name"
                    aria-invalid={!!error || undefined}
                    onChange={(event) => {
                        setDraft(event.target.value);
                        setError(undefined);
                    }}
                    onKeyDown={(event) => {
                        if (event.key === "Enter") {
                            event.preventDefault();
                            addAlias();
                        }
                    }}
                />
                <Button type="button" variant="outline" onClick={addAlias}>
                    Add
                </Button>
            </div>
        </FormField>
    );
}

function StripeLookupResult({
    finding,
    customer,
    notFound,
}: {
    finding: boolean;
    customer: StripeCustomer | null;
    notFound: boolean;
}) {
    if (finding) {
        return <Spinner />;
    }

    if (customer) {
        return (
            <div className="flex items-start gap-2">
                <Link className="mt-0.5 size-4 shrink-0 text-zinc-400" />
                <div className="flex min-w-0 flex-col gap-0.5">
                    <p className="text-xs text-zinc-900 leading-4">{customer.name}</p>
                    <p className="text-xs text-zinc-500 leading-4">{customer.email}</p>
                </div>
            </div>
        );
    }

    if (notFound) {
        return (
            <p className="text-xs text-zinc-900 leading-4">
                No matching customer in Stripe
            </p>
        );
    }

    return null;
}

type StripeLookupState =
    | { status: "idle" }
    | { status: "finding" }
    | { status: "resolved"; customer: StripeCustomer | null };

type StripeLookupAction =
    | { type: "find" }
    | { type: "typed" }
    | { type: "resolved"; customer: StripeCustomer | null };

function stripeLookupReducer(
    _state: StripeLookupState,
    action: StripeLookupAction,
): StripeLookupState {
    switch (action.type) {
        case "find":
            return { status: "finding" };
        case "typed":
            return { status: "idle" };
        case "resolved":
            return { status: "resolved", customer: action.customer };
        default: {
            const _exhaustive: never = action;
            return _exhaustive;
        }
    }
}

function stripeCustomerForLookup(
    lookup: StripeLookupState,
    stored: StripeCustomer | undefined,
): StripeCustomer | null {
    switch (lookup.status) {
        case "idle":
            return stored ?? null;
        case "finding":
            return null;
        case "resolved":
            return lookup.customer;
        default: {
            const _exhaustive: never = lookup;
            return _exhaustive;
        }
    }
}
